import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  BookOpen,
  BriefcaseBusiness,
  ChevronDown,
  Clock3,
  Focus,
  Layers3,
  Lightbulb,
  Minus,
  Plus,
  Sparkles,
  Target,
  X,
  ZoomIn,
} from "lucide-react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import "./PabsBrainLab.css";

export const PROTOTYPE_OBJECTS = [
  {
    id: "challenge",
    kind: "origin",
    eyebrow: "Project origin",
    title: "Make product strategy stay useful after it is created",
    preview: "Turn a strategic brief into a living system that changes with the project.",
    date: "12 Mar",
    icon: Sparkles,
    position: { x: 110, y: 410 },
    fields: [
      ["Design challenge", "How might we help founders keep product direction connected to what is actually learned and built?"],
      ["Outcome", "A strategy that remains understandable, traceable and current as the project moves."],
    ],
    versions: [],
  },
  {
    id: "audience",
    kind: "brief",
    eyebrow: "Brief · Audience",
    title: "Solo founders moving from idea to build",
    preview: "Decisive builders who need clarity without product-management theatre.",
    date: "18 Mar",
    icon: Target,
    position: { x: 550, y: 120 },
    fields: [
      ["Primary audience", "Solo founders and small founding teams moving directly from an idea into product execution."],
      ["What they need", "Enough strategic clarity to make a build decision, without maintaining a stack of static documents."],
      ["Risk if wrong", "The experience becomes strategy theatre instead of a practical layer around the work."],
    ],
    versions: [
      { date: "12 Mar", label: "First recorded", changed: true },
      { date: "15 Mar", label: "Recorded", changed: false },
      { date: "18 Mar", label: "Audience narrowed", changed: true },
    ],
  },
  {
    id: "vision",
    kind: "strategy",
    eyebrow: "Strategy · Design vision",
    title: "The project is the interface",
    preview: "Brief, choices, evidence and work exist in one navigable world.",
    date: "24 Apr",
    icon: Layers3,
    position: { x: 1010, y: 110 },
    fields: [
      ["Vision", "A founder should move through the project itself—not open separate documents that each describe a fragment of it."],
      ["Experience principle", "Every strategic object has a stable identity, a current state, a history and visible reasons for change."],
      ["Interaction", "Approaching an object reveals detail. Moving away restores the whole project without losing spatial context."],
    ],
    versions: [
      { date: "18 Mar", label: "Strategy created", changed: true },
      { date: "02 Apr", label: "Recorded", changed: false },
      { date: "11 Apr", label: "Learning applied", changed: true },
      { date: "24 Apr", label: "Current direction", changed: true },
    ],
  },
  {
    id: "hypothesis",
    kind: "strategy",
    eyebrow: "Strategy · Hypothesis",
    title: "Context before capability",
    preview: "People return when Pabs makes the next decision clearer, not when it generates more.",
    date: "29 Apr",
    icon: Lightbulb,
    position: { x: 1010, y: 420 },
    fields: [
      ["We believe", "Founders will treat Pabs as part of the project when it shows why the strategy changed and what that changes next."],
      ["We will know", "People revisit existing projects, inspect affected strategy and close the loop on learnings."],
      ["Still uncertain", "Whether the map becomes the primary project surface or a high-value way to inspect change."],
    ],
    versions: [
      { date: "18 Mar", label: "Hypothesis created", changed: true },
      { date: "29 Apr", label: "Success signal revised", changed: true },
    ],
  },
  {
    id: "learning-1",
    kind: "learning",
    eyebrow: "Learning · Applied",
    title: "Generated documents were not revisited",
    preview: "The value was in decisions and connections; the document containers became dead ends.",
    date: "11 Apr",
    icon: BookOpen,
    position: { x: 1420, y: 260 },
    fields: [
      ["Observed", "After generation, attention moved to the build. Users did not return simply to reread strategy documents."],
      ["Meaning", "Persistent value must come from showing what changed, why it matters and where the project now stands."],
      ["Applied to", "Design vision and the context-before-capability hypothesis."],
    ],
    versions: [],
  },
  {
    id: "job-1",
    kind: "job",
    eyebrow: "Job · Completed",
    title: "Trace build evidence back to strategy",
    preview: "Close the loop between what builders discover and what Pabs believes.",
    date: "22 Apr",
    icon: BriefcaseBusiness,
    position: { x: 1440, y: 570 },
    fields: [
      ["Purpose", "Give build agents a reliable way to report evidence and connect delivery back to the originating strategic requirement."],
      ["Result", "Learnings, decisions and delivery evidence now have a route back into the living strategy loop."],
      ["Affected", "The current hypothesis and how its success is observed."],
    ],
    versions: [],
  },
];

export const PROTOTYPE_RELATIONSHIPS = [
  ["challenge", "audience"],
  ["challenge", "vision"],
  ["challenge", "hypothesis"],
  ["audience", "vision"],
  ["learning-1", "vision"],
  ["learning-1", "hypothesis"],
  ["job-1", "hypothesis"],
];

const KIND_LABELS = {
  origin: "Origin",
  brief: "Brief",
  strategy: "Product strategy",
  learning: "Learning",
  job: "Job",
};

const asValue = (value) => {
  if (value == null) return null;
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return value; }
};

const asArray = (value) => {
  const parsed = asValue(value);
  if (Array.isArray(parsed)) return parsed;
  return parsed == null ? [] : [parsed];
};

const formatDate = (value) => value
  ? new Date(value).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })
  : "Current";

const stringifyComparable = (value) => JSON.stringify(asValue(value) ?? null);

const sectionVersions = (reports, key) => {
  let previous;
  return reports.map((report) => {
    const current = stringifyComparable(report[key]);
    const changed = previous === undefined || previous !== current;
    previous = current;
    return {
      date: formatDate(report.created_at),
      label: changed ? `Changed in V${report.version}` : `Recorded in V${report.version}`,
      changed,
      version: report.version,
    };
  });
};

const textFrom = (value, keys = []) => {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return "";
  for (const key of keys) {
    if (value[key]) return String(value[key]);
  }
  return Object.values(value).find((entry) => typeof entry === "string") || "";
};

const listFields = (items, titleKeys, detailKeys) => asArray(items).map((item, index) => {
  if (typeof item === "string") return [`${index + 1}`, item];
  const title = textFrom(item, titleKeys) || `Item ${index + 1}`;
  const details = detailKeys
    .map((key) => item?.[key] ? `${key.replaceAll("_", " ")}: ${Array.isArray(item[key]) ? item[key].join("; ") : item[key]}` : null)
    .filter(Boolean)
    .join("\n");
  return [title, details || textFrom(item)];
});

function buildPlan157Graph(plan, latest, reports, learnings, jobs = []) {
  const currentDate = formatDate(plan.updated_at);
  const briefObjects = [
    ["brief-summary", "Project Summary", plan.short_summary, BookOpen],
    ["brief-problem", "Problem Statement", plan.problem_statement, Target],
    ["brief-audience", "Audience", plan.target_statement, Target],
    ["brief-solution", "Possible Solution", plan.goal_statement, Lightbulb],
    ["brief-objectives", "Key Objectives", asArray(plan.objectives_statement).join("\n"), Target],
  ].filter(([, , value]) => value).map(([id, title, value, icon], index) => ({
    id,
    kind: "brief",
    eyebrow: `Brief · ${title}`,
    title,
    preview: String(value).replace(/\s+/g, " ").slice(0, 150),
    date: currentDate,
    icon,
    position: { x: 1150 + index * 410, y: 420 + (index % 2) * 260 },
    fields: [[title, String(value)]],
    versions: [],
    destination: "strategy",
  }));

  const strategySpecs = [
    ["ps-vision", "Design Vision", "design_vision", latest.design_vision, Layers3, [[
      "Vision", latest.design_vision || "",
    ]]],
    ["ps-hypotheses", "Design Hypotheses", "design_hypothesis", latest.design_hypothesis, Lightbulb,
      listFields(latest.design_hypothesis, ["hypothesis"], ["for_audience", "rationale", "success_indicator"])],
    ["ps-metrics", "Success Metrics", "success_metrics", latest.success_metrics, Target,
      listFields(latest.success_metrics, ["metric"], ["target", "rationale"])],
    ["ps-audience", "Audience Insights", "audience_insights", latest.audience_insights, Target,
      listFields(latest.audience_insights, ["audience"], ["context", "goals", "frustrations", "opportunities"])],
    ["ps-positioning", "Competitive Positioning", "competitive_ux_positioning", latest.competitive_ux_positioning, Layers3,
      listFields(latest.competitive_ux_positioning, ["positioning_statement", "summary"], ["key_differentiators"])],
    ["ps-opportunities", "Opportunity Areas", "opportunity_areas", latest.opportunity_areas, Sparkles,
      listFields(latest.opportunity_areas, ["opportunity"], ["rationale", "description"])],
    ["ps-themes", "Strategic Themes", "strategic_themes", latest.strategic_themes, Layers3,
      listFields(latest.strategic_themes, ["theme"], ["description", "rationale"])],
    ["ps-principles", "Experience Design Principles", "experience_design_principles", latest.experience_design_principles, Layers3,
      listFields(latest.experience_design_principles, ["principle"], ["description", "rationale"])],
    ["ps-assumptions", "Strategy Assumptions", "strategic_guidance", latest.strategic_guidance?.key_assumptions, Lightbulb,
      listFields(latest.strategic_guidance?.key_assumptions, ["assumption", "assumption_text"], ["rationale", "validation"])],
    ["ps-brief-analysis", "Brief Analysis", "brief_deconstruction", latest.brief_deconstruction, BookOpen,
      listFields(latest.brief_deconstruction, ["summary"], ["brief_gaps", "key_assumptions", "strategic_opportunities"])],
  ];

  let strategySectionIndex = 0;
  const strategyObjects = strategySpecs.flatMap(([sectionId, sectionTitle, historyKey, value, icon, fields]) => {
    if (!value || (typeof value === "object" && !Object.keys(value).length)) return [];
    const meaningfulFields = fields.filter(([, detail]) => detail);
    const splitItems = ["design_hypothesis", "success_metrics", "audience_insights", "opportunity_areas", "strategic_themes", "experience_design_principles"]
      .includes(historyKey);
    const index = strategySectionIndex++;
    const sectionX = 3300 + (index % 3) * 1450;
    const sectionY = 260 + Math.floor(index / 3) * 1050;
    const history = sectionVersions(reports, historyKey);

    if (!splitItems) {
      return {
        id: sectionId,
        sectionId,
        kind: "strategy",
        eyebrow: `Product Strategy · ${sectionTitle}`,
        title: sectionTitle,
        preview: textFrom(asArray(value)[0], [
          "hypothesis", "metric", "audience", "opportunity", "theme", "principle", "summary", "positioning_statement",
        ]).replace(/\s+/g, " ").slice(0, 180),
        date: formatDate(latest.updated_at || latest.created_at),
        icon,
        position: { x: sectionX, y: sectionY },
        fields: meaningfulFields,
        versions: history,
        destination: "productStrategy",
      };
    }

    const parent = {
      id: sectionId,
      sectionId,
      kind: "strategy-group",
      eyebrow: "Product Strategy · Object group",
      title: sectionTitle,
      preview: `${meaningfulFields.length} independent objects`,
      date: formatDate(latest.updated_at || latest.created_at),
      icon,
      position: { x: sectionX, y: sectionY },
      fields: [["Contains", meaningfulFields.map(([title]) => title).join("\n")]],
      versions: history,
      destination: "productStrategy",
    };
    const children = meaningfulFields.map(([itemTitle, itemValue], itemIndex) => ({
      id: `${sectionId}-${itemIndex + 1}`,
      sectionId,
      kind: "strategy",
      eyebrow: `Product Strategy · ${sectionTitle}`,
      title: `${sectionTitle.replace(/s$/, "")} ${itemIndex + 1}`,
      preview: `${itemTitle} ${itemValue}`.replace(/\s+/g, " ").slice(0, 180),
      searchText: `${itemTitle} ${itemValue}`.replace(/\s+/g, " ").toLowerCase(),
      date: formatDate(latest.updated_at || latest.created_at),
      icon,
      position: {
        x: sectionX + 520 + (itemIndex % 2) * 430,
        y: sectionY - 260 + Math.floor(itemIndex / 2) * 300 + (itemIndex % 2) * 120,
      },
      fields: [[itemTitle, itemValue]],
      versions: history,
      destination: "productStrategy",
    }));
    return [parent, ...children];
  });

  const learningObjects = (learnings || []).map((learning, index) => ({
    id: `learning-${learning.id}`,
    kind: "learning",
    eyebrow: `Learning · ${learning.status}`,
    title: `Learning #${learning.id}`,
    preview: learning.learning,
    date: formatDate(learning.decided_at || learning.created_at),
    icon: BookOpen,
    position: { x: 2800 + (index % 4) * 320, y: 2050 + Math.floor(index / 4) * 180 },
    fields: [
      ["Observed", learning.learning],
      ["Strategic reasoning", learning.strategic_reasoning || ""],
      ["Decision", learning.status],
    ].filter(([, value]) => value),
    versions: [],
    isTimelineEvent: true,
  }));
  const visibleJobs = (jobs || []).filter((job) =>
    job.record_type === "task" &&
    !["refresh_recommended_next_steps", "next_step_status_update", "decision_capture"].includes(job.capability)
  );
  const jobObjects = visibleJobs.map((job, index) => ({
    id: `job-${job.id}`,
    kind: "job",
    eyebrow: `Job · ${job.status}`,
    title: job.task_type || `Job #${job.id}`,
    preview: job.reasoning || job.parameters?.requirement || "",
    date: formatDate(job.completed_at || job.created_at),
    icon: BriefcaseBusiness,
    position: { x: 2450 + (index % 5) * 290, y: 2700 + Math.floor(index / 5) * 170 },
    fields: [
      ["Purpose", job.reasoning || ""],
      ["Status", job.status],
      ["Capability", job.capability || ""],
    ].filter(([, value]) => value),
    versions: [],
    isTimelineEvent: true,
  }));

  const origin = {
    id: "challenge",
    kind: "origin",
    eyebrow: "Project origin · Design challenge",
    title: plan.plan_name,
    preview: plan.design_challenge,
    date: formatDate(plan.created_at),
    icon: Sparkles,
    position: { x: 160, y: 470 },
    fields: [["Design challenge", plan.design_challenge]],
    versions: [],
  };

  const phaseObjects = [
    {
      id: "phase-brief",
      kind: "phase",
      eyebrow: "Project landmark",
      title: "Brief",
      preview: `${briefObjects.length} current project objects`,
      date: formatDate(plan.updated_at),
      icon: BookOpen,
      position: { x: 950, y: 470 },
      fields: [["Contains", briefObjects.map((item) => item.title).join(", ")]],
      versions: [],
    },
    {
      id: "phase-strategy",
      kind: "phase",
      eyebrow: "Project landmark",
      title: "Product Strategy",
      preview: `${strategyObjects.filter((item) => item.kind !== "strategy-group").length} strategic objects · V${latest.version}`,
      date: formatDate(latest.created_at),
      icon: Layers3,
      position: { x: 2900, y: 470 },
      fields: [["Current report", `Version ${latest.version}`]],
      versions: sectionVersions(reports, "id"),
    },
  ];
  const relationships = [
    ["challenge", "phase-brief"],
    ["phase-brief", "phase-strategy"],
    ...briefObjects.map((item) => ["phase-brief", item.id]),
    ...strategyObjects
      .filter((item) => item.id === item.sectionId)
      .map((item) => ["phase-strategy", item.id]),
    ...strategyObjects
      .filter((item) => item.id !== item.sectionId)
      .map((item) => [item.sectionId, item.id]),
  ];
  const typeTargets = {
    hypothesis: "ps-hypotheses",
    metric: "ps-metrics",
    success_metrics: "ps-metrics",
    design_vision: "ps-vision",
    assumption: "ps-assumptions",
    positioning: "ps-positioning",
    audience: "ps-audience",
    opportunity: "ps-opportunities",
  };
  (learnings || []).forEach((learning) => {
    asArray(learning.affected_elements).forEach((affected) => {
      const targetSection = typeTargets[String(affected?.element_type || "").toLowerCase()];
      if (!targetSection) return;
      const evidenceText = `${affected?.element_text || ""} ${affected?.element_ref || ""}`
        .replace(/\s+/g, " ")
        .toLowerCase();
      const sectionItems = strategyObjects.filter(
        (item) => item.sectionId === targetSection && item.id !== targetSection
      );
      const specificTarget = sectionItems.find((item) => {
        if (!item.searchText || !evidenceText) return false;
        const evidenceNeedle = evidenceText.slice(0, 90);
        const itemNeedle = item.searchText.slice(0, 90);
        return item.searchText.includes(evidenceNeedle) || evidenceText.includes(itemNeedle);
      });
      const target = specificTarget || strategyObjects.find((item) => item.id === targetSection);
      if (target) relationships.push([`learning-${learning.id}`, target.id]);
    });
  });

  // Time belongs to objects; it does not determine canvas coordinates.
  // Place evidence beside the semantic objects it affects. Shared evidence
  // sits between its targets. Unlinked activity remains near the project hub.
  const semanticObjects = [origin, ...phaseObjects, ...briefObjects, ...strategyObjects];
  const semanticPositions = new Map(semanticObjects.map((item) => [item.id, item.position]));
  learningObjects.forEach((learning, index) => {
    const targetPositions = relationships
      .filter(([source]) => source === learning.id)
      .map(([, target]) => semanticPositions.get(target))
      .filter(Boolean);
    if (!targetPositions.length) return;
    const centre = targetPositions.reduce(
      (sum, position) => ({ x: sum.x + position.x, y: sum.y + position.y }),
      { x: 0, y: 0 }
    );
    learning.position = {
      x: centre.x / targetPositions.length + 420 + (index % 3) * 85,
      y: centre.y / targetPositions.length - 180 + (index % 4) * 120,
    };
  });

  return {
    objects: [origin, ...phaseObjects, ...briefObjects, ...strategyObjects, ...learningObjects, ...jobObjects],
    relationships: [...new Map(relationships.map((relationship) => [relationship.join("::"), relationship])).values()],
  };
}

function pathBetween(a, b) {
  const startX = a.x < b.x ? a.x + a.width : a.x;
  const endX = a.x < b.x ? b.x : b.x + b.width;
  const startY = a.y + a.anchorY;
  const endY = b.y + b.anchorY;
  const bend = Math.max(90, Math.abs(endX - startX) * 0.42);
  const direction = endX >= startX ? 1 : -1;
  return `M ${startX} ${startY} C ${startX + bend * direction} ${startY}, ${endX - bend * direction} ${endY}, ${endX} ${endY}`;
}

function connectorBounds(item, selectedId, relatedIds) {
  const selected = item.id === selectedId;
  const marker = item.isTimelineEvent && !selected && !relatedIds.has(item.id);
  const width = selected ? 410 : marker ? 46 : item.kind === "phase" ? 340 : item.kind === "strategy-group" ? 330 : 286;
  const height = selected ? 410 : marker ? 46 : item.kind === "phase" ? 150 : item.kind === "strategy-group" ? 148 : 132;
  return {
    ...item.renderPosition,
    width,
    height,
    anchorY: height / 2,
  };
}

function ProjectCard({ item, selected, related, dimmed, zoom, onSelect }) {
  const Icon = item.icon;
  const detailed = selected || zoom > 0.86;
  const marker = item.isTimelineEvent && !selected && !related;
  return (
    <article
      className={`brain-card brain-card--${item.kind} ${selected ? "is-selected" : ""} ${related ? "is-related" : ""} ${dimmed ? "is-dimmed" : ""} ${marker ? "is-timeline-marker" : ""}`}
      style={{ transform: `translate3d(${item.renderPosition.x}px, ${item.renderPosition.y}px, 0)` }}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(item.id);
      }}
      aria-label={`Open ${item.title}`}
    >
      {!marker && <><span className="brain-card__port brain-card__port--left" /><span className="brain-card__port brain-card__port--right" /></>}
      <div className="brain-card__topline">
        <span className="brain-card__icon"><Icon size={15} strokeWidth={1.8} /></span>
        <span>{item.eyebrow}</span>
        <time>{item.date}</time>
      </div>
      <h2>{item.title}</h2>
      <p className="brain-card__preview">{item.preview}</p>
      {detailed && !selected && (
        <div className="brain-card__meta">
          <span>{item.versions.length ? `${item.versions.length} moments` : "Current"}</span>
          <span>Open <ZoomIn size={12} /></span>
        </div>
      )}
      {selected && (
        <div className="brain-card__expanded">
          {item.fields.map(([label, value]) => (
            <section key={label}>
              <h3>{label}</h3>
              <p>{value}</p>
            </section>
          ))}
          <div className="brain-card__footer">
            <span>Current state</span>
            <span>Updated {item.date}</span>
          </div>
        </div>
      )}
    </article>
  );
}

export default function PabsBrainLab() {
  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  const [graph, setGraph] = useState({ objects: [], relationships: [] });
  const [project, setProject] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [selectedId, setSelectedId] = useState(null);
  const [showNavigator, setShowNavigator] = useState(false);
  const [transform, setTransform] = useState({ x: 50, y: 120, scale: 0.72 });
  const objects = graph.objects;
  const relationships = graph.relationships;
  const selected = objects.find((item) => item.id === selectedId);

  useEffect(() => {
    let cancelled = false;
    const planId = new URLSearchParams(window.location.search).get("plan") || "157";
    const loadGraph = async () => {
      setLoadState("loading");
      try {
        const planResponse = await fetchWithAuth(`/api/plans/${planId}`);
        if (!planResponse.ok) throw new Error("The project could not be loaded.");
        const planPayload = await planResponse.json();
        const [latestResponse, historyResponse, learningsResponse, activityResponse] = await Promise.all([
          fetchWithAuth(`/api/product-strategy/plan/${planId}/latest`),
          fetchWithAuth(`/api/product-strategy/plan/${planId}`),
          fetchWithAuth(`/api/learnings/${planId}`),
          fetchWithAuth(`/api/agent/tasks/user/${planPayload.user_id}`),
        ]);
        if (![latestResponse, historyResponse, learningsResponse, activityResponse].every((response) => response.ok)) {
          throw new Error("The project data could not be loaded.");
        }
        const [latestPayload, historyPayload, learningsPayload, activityPayload] = await Promise.all([
          latestResponse.json(),
          historyResponse.json(),
          learningsResponse.json(),
          activityResponse.json(),
        ]);
        if (!latestPayload.report) throw new Error("Plan 157 has no Product Strategy report.");
        if (cancelled) return;
        const planActivity = (activityPayload.plans || []).find((entry) => Number(entry.plan_id) === Number(planId));
        setProject(planPayload);
        setGraph(buildPlan157Graph(
          planPayload,
          latestPayload.report,
          historyPayload.reports || [],
          Array.isArray(learningsPayload) ? learningsPayload : (learningsPayload.learnings || []),
          planActivity?.items || []
        ));
        setLoadState("ready");
      } catch (error) {
        if (cancelled) return;
        console.error("[PabsBrainLab] Failed to load real plan data:", error);
        setLoadState("error");
      }
    };
    loadGraph();
    return () => { cancelled = true; };
  }, []);

  const relatedIds = useMemo(() => {
    if (!selectedId) return new Set();
    const ids = new Set([selectedId]);
    relationships.forEach(([a, b]) => {
      if (a === selectedId) ids.add(b);
      if (b === selectedId) ids.add(a);
    });
    return ids;
  }, [relationships, selectedId]);

  const displayObjects = useMemo(() => {
    return objects.map((item) => ({ ...item, renderPosition: item.position }));
  }, [objects]);

  const positionMap = useMemo(
    () => new Map(displayObjects.map((item) => [
      item.id,
      connectorBounds(item, selectedId, relatedIds),
    ])),
    [displayObjects, relatedIds, selectedId]
  );

  const focusOverview = useCallback(() => {
    setSelectedId(null);
    setTransform({ x: 50, y: 120, scale: 0.72 });
  }, []);

  const selectObject = useCallback((id) => {
    const item = objects.find((candidate) => candidate.id === id);
    if (item?.kind === "phase") {
      setSelectedId(null);
      const scale = 0.84;
      const width = viewportRef.current?.clientWidth || window.innerWidth;
      const height = viewportRef.current?.clientHeight || window.innerHeight;
      setTransform({
        x: width * 0.38 - item.position.x * scale,
        y: height * 0.48 - item.position.y * scale,
        scale,
      });
      return;
    }
    setSelectedId(id);
    if (!item) return;
    const scale = 1;
    const width = viewportRef.current?.clientWidth || window.innerWidth;
    const height = viewportRef.current?.clientHeight || window.innerHeight;
    const navigatorAllowance = showNavigator ? 300 : 0;
    setTransform({
      x: (width - navigatorAllowance) * 0.5 - (item.position.x + 205) * scale,
      y: height * 0.5 - (item.position.y + 205) * scale,
      scale,
    });
  }, [objects, showNavigator]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") focusOverview();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [focusOverview]);

  const changeZoom = (delta, point) => {
    setTransform((current) => {
      const nextScale = Math.min(1.28, Math.max(0.48, current.scale + delta));
      if (!point) return { ...current, scale: nextScale };
      const ratio = nextScale / current.scale;
      return {
        scale: nextScale,
        x: point.x - (point.x - current.x) * ratio,
        y: point.y - (point.y - current.y) * ratio,
      };
    });
  };

  if (loadState !== "ready") {
    return (
      <main className="brain-lab brain-lab--loading">
        <div className="brain-load-card">
          <span className="brain-mark">p</span>
          {loadState === "loading" ? (
            <>
              <strong>Loading Plan 157</strong>
              <p>Reading the real Brief, Product Strategy history and learning relationships.</p>
            </>
          ) : (
            <>
              <strong>Plan 157 could not be loaded</strong>
              <p>Sign in with access to the plan, then reopen this experiment.</p>
            </>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="brain-lab">
      <header className="brain-header">
        <div className="brain-header__identity">
          <button className="icon-button" onClick={() => window.history.back()} aria-label="Go back"><ArrowLeft size={18} /></button>
          <span className="brain-mark">p</span>
          <div>
            <strong>Pabs Brain</strong>
            <span>Living strategy experiment</span>
          </div>
        </div>
        <div className="brain-header__project">
          <span className="live-dot" />
          <div>
            <strong>{project?.plan_name}</strong>
            <span>Alive · updated {formatDate(project?.updated_at)}</span>
          </div>
        </div>
        <div className="brain-header__actions">
          <button className={showNavigator ? "is-active" : ""} onClick={() => setShowNavigator((value) => !value)}>
            <Layers3 size={15} /> Objects
          </button>
          <button onClick={focusOverview}><Focus size={15} /> See whole project</button>
        </div>
      </header>

      <section
        ref={viewportRef}
        className={`brain-viewport ${selectedId ? "has-selection" : ""}`}
        onWheel={(event) => {
          event.preventDefault();
          const bounds = viewportRef.current.getBoundingClientRect();
          changeZoom(event.deltaY > 0 ? -0.08 : 0.08, { x: event.clientX - bounds.left, y: event.clientY - bounds.top });
        }}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          dragRef.current = { x: event.clientX, y: event.clientY, originX: transform.x, originY: transform.y };
        }}
        onPointerMove={(event) => {
          if (!dragRef.current) return;
          setTransform((current) => ({
            ...current,
            x: dragRef.current.originX + event.clientX - dragRef.current.x,
            y: dragRef.current.originY + event.clientY - dragRef.current.y,
          }));
        }}
        onPointerUp={() => { dragRef.current = null; }}
        onPointerCancel={() => { dragRef.current = null; }}
        onClick={() => selectedId && focusOverview()}
      >
        <div
          className="brain-scene"
          style={{ transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})` }}
        >
          <div className="brain-era brain-era--origin"><span>Project origin</span></div>
          <div className="brain-era brain-era--direction"><span>Brief space</span></div>
          <div className="brain-era brain-era--evidence"><span>Strategy space</span></div>
          <svg className="brain-links" width="1800" height="940" aria-hidden="true">
            {relationships.map(([a, b]) => {
              if (!positionMap.has(a) || !positionMap.has(b)) return null;
              const active = !selectedId || (relatedIds.has(a) && relatedIds.has(b));
              return <path key={`${a}-${b}`} d={pathBetween(positionMap.get(a), positionMap.get(b))} className={active ? "is-active" : "is-muted"} />;
            })}
          </svg>
          {displayObjects.map((item) => (
            <ProjectCard
              key={item.id}
              item={item}
              selected={item.id === selectedId}
              related={selectedId && relatedIds.has(item.id)}
              dimmed={selectedId && !relatedIds.has(item.id)}
              zoom={transform.scale}
              onSelect={selectObject}
            />
          ))}
          {selected && selected.versions.length > 0 && (
            <div
              className="history-trail"
              style={{ transform: `translate3d(${selected.position.x - 275}px, ${selected.position.y + 20}px, 0)` }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="history-trail__label">
                <Clock3 size={14} />
                <span>{selected.versions.length} recorded moments</span>
                <strong>{selected.versions.filter((version) => version.changed).length} updates</strong>
              </div>
              {selected.versions.map((version, index) => (
                <button key={`${version.date}-${index}`} className={version.changed ? "did-change" : "recorded-only"}>
                  <span className="history-dot" />
                  <time>{version.date}</time>
                  <small>{version.label}</small>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="brain-context">
          {selected ? (
            <>
              <span>{KIND_LABELS[selected.kind]}</span>
              <strong>{selected.title}</strong>
              <button onClick={focusOverview}><X size={14} /> Close focus</button>
            </>
          ) : (
            <>
              <span>Explore the project</span>
              <strong>Scroll to zoom · drag to move · select any object</strong>
            </>
          )}
        </div>

        <div className="zoom-controls">
          <button onClick={() => changeZoom(0.1)} aria-label="Zoom in"><Plus size={16} /></button>
          <span>{Math.round(transform.scale * 100)}%</span>
          <button onClick={() => changeZoom(-0.1)} aria-label="Zoom out"><Minus size={16} /></button>
        </div>

      </section>

      <aside className={`brain-navigator ${showNavigator ? "is-open" : ""}`}>
        <div className="brain-navigator__head">
          <div>
            <span>Project objects</span>
            <strong>Current frontier</strong>
          </div>
          <button onClick={() => setShowNavigator(false)} aria-label="Close objects"><X size={16} /></button>
        </div>
        {["brief", "strategy"].map((kind) => (
          <section key={kind}>
            <button className="brain-navigator__section">
              <span>{KIND_LABELS[kind]}</span>
              <ChevronDown size={14} />
            </button>
            {objects.filter((item) => item.kind === kind).map((item) => (
              <button key={item.id} className={item.id === selectedId ? "object-row is-current" : "object-row"} onClick={() => selectObject(item.id)}>
                <span className={`object-row__dot object-row__dot--${kind}`} />
                <span>
                  <strong>{item.title}</strong>
                  <small>{item.versions.length} moments · updated {item.date}</small>
                </span>
              </button>
            ))}
          </section>
        ))}
        <div className="brain-navigator__footer">
          <Clock3 size={14} />
          <span>{objects.length} objects · {objects.filter((item) => item.kind === "learning").length} learnings</span>
        </div>
      </aside>
    </main>
  );
}
